import { useCallback, useEffect, useRef, useState } from "react";
import { getInvoiceStatus } from "../api";
import { POLL_INTERVAL_MS } from "../config";

export type InvoiceStatus = "idle" | "pending" | "confirmed" | "failed";

// Polls the backend every POLL_INTERVAL_MS for payment confirmation.
export function useInvoicePoller() {
  const [status, setStatus] = useState<InvoiceStatus>("idle");
  const timer = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(
    (invoiceId: string) => {
      stop();
      setStatus("pending");
      const poll = async () => {
        try {
          const { status: remote } = await getInvoiceStatus(invoiceId);
          if (remote === "confirmed" || remote === "failed") {
            setStatus(remote);
            stop();
          }
        } catch {
          // Network blip — keep polling on the next tick.
        }
      };
      void poll();
      timer.current = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    },
    [stop],
  );

  useEffect(() => stop, [stop]);

  return { status, start, stop };
}
